export default function HomePage() {

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    
    const name = formData.get('name') as string;
    const email = formData.get('email') as string;
    const message = formData.get('message') as string;

    if (!name || !email || !message) {
      alert('Please fill in all fields');
      return;
    }

    if (message.length > 500) {
      alert('Message must be 500 characters or less');
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d6eq663v53dtlm7j9b8g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        alert('Message sent successfully!');
        form.reset();
      } else {
        alert('Failed to send message. Please try again.');
      }
    } catch (error) {
      alert('An error occurred. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center px-6 md:px-12 lg:px-20 py-20">
        <div className="max-w-7xl mx-auto w-full">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
            {/* Left Content Zone */}
            <div className="w-full lg:w-[45%] space-y-12">
              <div className="space-y-20">
                <h2 className="text-sm uppercase tracking-[2px] font-medium text-[#1a1a1a]">
                  GALA D.O.O.
                </h2>
                
                <div className="space-y-6">
                  <h1 className="text-5xl md:text-6xl lg:text-7xl leading-tight">
                    <span className="block font-extrabold text-black">Modern iOS Applications</span>
                    <span className="block font-light text-[#6b6b6b]">Built for Security</span>
                    <span className="block font-extrabold text-black">& Performance</span>
                  </h1>
                  
                  <p className="text-lg text-[#4a4a4a] leading-relaxed max-w-[420px]">
                    Expert team developing VPN and Cleaner solutions that enhance mobile experience and protect user privacy on iOS devices
                  </p>
                </div>
              </div>
            </div>

            {/* Right Visual Zone */}
            <div className="w-full lg:w-[55%] lg:mt-10">
              <div className="relative rounded-[32px] bg-gradient-to-br from-[#8b7fb8] via-[#a89dd4] to-[#c4b8e8] p-12 md:p-16 lg:p-20 shadow-inner min-h-[400px] md:min-h-[500px] flex items-center justify-center">
                <img 
                  src="https://public.readdy.ai/ai/img_res/e9ceb796-9cea-43df-b422-a860a39eb842.png" 
                  alt="GALA D.O.O. Logo" 
                  className="w-full max-w-[300px] h-auto object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="relative py-20 md:py-32 px-6 md:px-12 lg:px-20 bg-[#fafafa]">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-end mb-8">
            <div className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-full">
              <span className="text-[11px] uppercase tracking-[1.5px] text-gray-600">EXPERTISE</span>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
            {/* Left Image Cluster */}
            <div className="w-full lg:w-[55%] flex flex-col justify-start">
              {/* Image Cluster */}
              <div className="relative h-[400px] md:h-[500px]">
                <div className="absolute top-0 right-0 w-[280px] h-[400px] rounded-[24px] border-8 border-white shadow-lg overflow-hidden z-10">
                  <img 
                    src="https://public.readdy.ai/ai/img_res/870c31b659f5c8c5fe3a5d7b69490123.jpg"
                    alt="VPN App Interface"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute bottom-0 left-0 w-[340px] h-[340px] rounded-[24px] border-8 border-white shadow-lg overflow-hidden">
                  <img 
                    src="https://readdy.ai/api/search-image?query=iOS%20device%20cleaner%20application%20dashboard%20showing%20storage%20optimization%20and%20performance%20metrics%2C%20modern%20interface%20design%2C%20clean%20white%20background%2C%20professional%20product%20photography%2C%20minimalist%20style&width=340&height=340&seq=about2&orientation=squarish"
                    alt="Cleaner App Dashboard"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>

            {/* Right Text Column */}
            <div className="w-full lg:w-[40%] flex flex-col justify-start">
              <h2 className="text-4xl md:text-5xl leading-tight mb-12">
                <span className="block font-bold text-black">Who We Are</span>
                <span className="block text-[#8b7fb8]">& What We Build 🛡️</span>
              </h2>
              
              <p className="text-base text-[#3d3d3d] leading-relaxed mb-10 max-w-[380px]">
                GALA D.O.O. is a dedicated team of iOS development experts committed to creating applications that prioritize user security and device performance. Our flagship products, VPN and Cleaner, represent years of expertise in mobile development and a deep understanding of iOS ecosystem requirements.
              </p>

              <p className="text-base text-[#3d3d3d] leading-relaxed mb-10 max-w-[380px]">
                We believe in building tools that not only solve problems but enhance the overall mobile experience. Every line of code is crafted with attention to detail, ensuring our applications meet the highest standards of quality and reliability.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="relative py-20 md:py-32 px-6 md:px-12 lg:px-20">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-20 gap-6">
            <h2 className="text-5xl md:text-6xl font-extrabold text-black leading-tight">
              Our<br />Products
            </h2>
            <p className="text-base text-gray-600 max-w-md">
              Innovative solutions designed to protect and optimize your iOS experience
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-10 gap-6">
            {/* VPN Product Card */}
            <div className="lg:col-span-4 bg-[#f5f5f5] rounded-[20px] p-10" data-product-shop>
              <div className="mb-8 h-48 rounded-2xl bg-gradient-to-br from-[#8b7fb8] to-[#6b5fa8] flex items-center justify-center overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=abstract%20visualization%20of%20encrypted%20VPN%20connection%20with%20flowing%20purple%20and%20blue%20gradient%20waves%2C%20digital%20security%20concept%2C%20modern%20minimalist%20style%2C%20clean%20simple%20background%2C%20geometric%20shapes&width=400&height=300&seq=vpn1&orientation=landscape"
                  alt="VPN Protection Visualization"
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="text-3xl font-bold text-black mb-4 text-left">VPN Protection</h3>
              <p className="text-[15px] text-[#5a5a5a] leading-relaxed">
                Secure browsing with military-grade encryption, global server network, and zero-log policy
              </p>
            </div>

            {/* Cleaner Product Card */}
            <div className="lg:col-span-3 bg-[#f5f5f5] rounded-[20px] p-10" data-product-shop>
              <h3 className="text-2xl font-bold text-black mb-6 text-center">Device Cleaner</h3>
              <div className="h-48 flex items-center justify-center">
                <img 
                  src="https://readdy.ai/api/search-image?query=abstract%20circular%20progress%20rings%20showing%20storage%20optimization%20in%20gradient%20purple%20and%20blue%20colors%2C%20clean%20minimalist%20design%2C%20simple%20light%20background%2C%20modern%20digital%20interface%20concept&width=300&height=300&seq=cleaner1&orientation=squarish"
                  alt="Storage Optimization"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>

            {/* Performance Card */}
            <div className="lg:col-span-3 bg-[#f5f5f5] rounded-[20px] p-10">
              <h3 className="text-2xl font-bold text-black mb-6 text-center">Performance Boost</h3>
              <div className="h-48 flex items-center justify-center">
                <img 
                  src="https://readdy.ai/api/search-image?query=modern%20speed%20meter%20gauge%20with%20gradient%20needle%20pointing%20to%20optimal%20performance%20zone%2C%20purple%20and%20blue%20colors%2C%20minimalist%20design%2C%20clean%20simple%20background%2C%20digital%20dashboard%20concept&width=300&height=300&seq=performance1&orientation=squarish"
                  alt="Performance Meter"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Applications Grid */}
      <section className="relative py-20 md:py-32 px-6 md:px-12 lg:px-20">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-14">Featured Applications</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-product-shop>
            {/* VPN Pro */}
            <div className="bg-[#f7f7f7] rounded-2xl overflow-hidden hover:shadow-lg transition-shadow">
              <div className="relative p-4">
                <div className="absolute top-4 left-4 bg-[#1a1a1a] rounded-xl px-3 py-1.5 z-10">
                  <span className="text-white text-[10px] uppercase tracking-wide">FEATURED</span>
                </div>
                <div className="bg-gradient-to-br from-[#8b7fb8] to-[#6b5fa8] rounded-xl p-12 flex items-center justify-center h-48">
                  <div className="w-16 h-16 flex items-center justify-center">
                    <i className="ri-shield-check-line text-white text-6xl"></i>
                  </div>
                </div>
              </div>
              <div className="p-5 text-center bg-white">
                <h3 className="text-xl font-bold text-black mb-2">VPN Pro</h3>
                <p className="text-[15px] text-[#666666]">Premium Security</p>
              </div>
            </div>

            {/* Cleaner Plus */}
            <div className="bg-[#f7f7f7] rounded-2xl overflow-hidden hover:shadow-lg transition-shadow">
              <div className="p-4">
                <div className="bg-gradient-to-br from-[#a89dd4] to-[#8b7fb8] rounded-xl p-12 flex items-center justify-center h-48">
                  <div className="w-16 h-16 flex items-center justify-center">
                    <i className="ri-brush-line text-white text-6xl"></i>
                  </div>
                </div>
              </div>
              <div className="p-5 text-center bg-white">
                <h3 className="text-xl font-bold text-black mb-2">Cleaner Plus</h3>
                <p className="text-[15px] text-[#666666]">Storage Optimizer</p>
              </div>
            </div>

            {/* VPN Basic */}
            <div className="bg-[#f7f7f7] rounded-2xl overflow-hidden hover:shadow-lg transition-shadow">
              <div className="p-4">
                <div className="bg-gradient-to-br from-[#c4b8e8] to-[#a89dd4] rounded-xl p-12 flex items-center justify-center h-48">
                  <div className="w-16 h-16 flex items-center justify-center">
                    <i className="ri-lock-line text-white text-6xl"></i>
                  </div>
                </div>
              </div>
              <div className="p-5 text-center bg-white">
                <h3 className="text-xl font-bold text-black mb-2">VPN Basic</h3>
                <p className="text-[15px] text-[#666666]">Essential Protection</p>
              </div>
            </div>

            {/* Cleaner Lite */}
            <div className="bg-[#f7f7f7] rounded-2xl overflow-hidden hover:shadow-lg transition-shadow">
              <div className="p-4">
                <div className="bg-gradient-to-br from-[#8b7fb8] to-[#7a6fa8] rounded-xl p-12 flex items-center justify-center h-48">
                  <div className="w-16 h-16 flex items-center justify-center">
                    <i className="ri-delete-bin-line text-white text-6xl"></i>
                  </div>
                </div>
              </div>
              <div className="p-5 text-center bg-white">
                <h3 className="text-xl font-bold text-black mb-2">Cleaner Lite</h3>
                <p className="text-[15px] text-[#666666]">Quick Clean</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-20 md:py-24 px-6 md:px-12 lg:px-20 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="w-[85%] mx-auto h-[480px] rounded-[28px] overflow-hidden mb-14 relative">
            <img 
              src="https://readdy.ai/api/search-image?query=person%20using%20iPhone%20in%20modern%20minimalist%20workspace%20with%20natural%20soft%20lighting%2C%20clean%20desk%20setup%2C%20professional%20lifestyle%20photography%2C%20warm%20tones%2C%20film%20grain%20texture%2C%20artistic%20quality%2C%20simple%20background&width=1400&height=480&seq=cta1&orientation=landscape"
              alt="Modern Workspace"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
          </div>

          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl leading-relaxed">
              <span className="text-[#9a9a9a] italic">Experience the future</span><br />
              <span className="text-black font-bold">of mobile security</span><br />
              <span className="text-[#9a9a9a] italic">and performance</span>
            </h2>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="relative py-20 md:py-32 px-6 md:px-12 lg:px-20 bg-[#f4f4f4]">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
            {/* Left Column */}
            <div className="w-full lg:w-[40%]">
              <h2 className="text-2xl font-bold text-black mb-8">GALA D.O.O.</h2>
              <p className="text-[15px] text-[#5d5d5d] leading-relaxed mb-6">
                A team of experts developing modern applications for the iOS platform. Our products are designed to enhance user experience and improve the security of mobile devices.
              </p>
              <p className="text-[15px] text-[#5d5d5d] leading-relaxed">
                We are committed to delivering high-quality solutions that meet the evolving needs of iOS users worldwide.
              </p>
            </div>

            {/* Right Column - Three Columns */}
            <div className="w-full lg:w-[60%] grid grid-cols-1 sm:grid-cols-3 gap-10">
              {/* Contact Information */}
              <div>
                <h3 className="text-xs uppercase text-gray-600 tracking-wide mb-5">CONTACT</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-2">
                    <div className="w-4 h-4 flex items-center justify-center mt-0.5">
                      <i className="ri-mail-line text-[#3a3a3a] text-base"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-[#3a3a3a] mb-1">Email</div>
                      <a href="mailto:contact@gala-doo.com" className="text-[15px] text-[#3a3a3a] hover:text-[#8b7fb8] transition-colors">
                        contact@gala-doo.com
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-4 h-4 flex items-center justify-center mt-0.5">
                      <i className="ri-phone-line text-[#3a3a3a] text-base"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-[#3a3a3a] mb-1">Phone</div>
                      <a href="tel:+19472247010" className="text-[15px] text-[#3a3a3a] hover:text-[#8b7fb8] transition-colors">
                        +1 9472247010
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Location Details */}
              <div>
                <h3 className="text-xs uppercase text-gray-600 tracking-wide mb-5">LOCATION</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-2">
                    <div className="w-4 h-4 flex items-center justify-center mt-0.5">
                      <i className="ri-map-pin-line text-[#3a3a3a] text-base"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-[#3a3a3a] mb-1">Address</div>
                      <div className="text-[15px] text-[#3a3a3a]">TRIGLAVSKA 7/12</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-4 h-4 flex items-center justify-center mt-0.5">
                      <i className="ri-building-line text-[#3a3a3a] text-base"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-[#3a3a3a] mb-1">City</div>
                      <div className="text-[15px] text-[#3a3a3a]">Nis (Palilula)</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-4 h-4 flex items-center justify-center mt-0.5">
                      <i className="ri-mail-send-line text-[#3a3a3a] text-base"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-[#3a3a3a] mb-1">Zip Code</div>
                      <div className="text-[15px] text-[#3a3a3a]">701702</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-4 h-4 flex items-center justify-center mt-0.5">
                      <i className="ri-global-line text-[#3a3a3a] text-base"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-[#3a3a3a] mb-1">Country</div>
                      <div className="text-[15px] text-[#3a3a3a]">Serbia</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div>
                <h3 className="text-xs uppercase text-gray-600 tracking-wide mb-5">GET IN TOUCH</h3>
                <form id="contact-form" onSubmit={handleSubmit} data-readdy-form className="space-y-4">
                  <div>
                    <input
                      type="text"
                      name="name"
                      placeholder="Your Name"
                      required
                      className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-[#8b7fb8] transition-colors"
                    />
                  </div>
                  <div>
                    <input
                      type="email"
                      name="email"
                      placeholder="Your Email"
                      required
                      className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-[#8b7fb8] transition-colors"
                    />
                  </div>
                  <div>
                    <textarea
                      name="message"
                      placeholder="Your Message"
                      required
                      maxLength={500}
                      rows={3}
                      className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-[#8b7fb8] transition-colors resize-none"
                    ></textarea>
                    <div className="text-xs text-gray-500 mt-1">Max 500 characters</div>
                  </div>
                  <button
                    type="submit"
                    className="w-full bg-[#1a1a1a] text-white py-2.5 rounded-lg text-sm font-medium hover:bg-[#2a2a2a] transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Send Message
                  </button>
                </form>
              </div>
            </div>
          </div>

          {/* Footer Bottom */}
          <div className="mt-16 pt-8 border-t border-gray-300 text-center">
            <p className="text-sm text-[#5d5d5d]">
              © 2025 GALA D.O.O. All rights reserved. | <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-[#8b7fb8] transition-colors">Website Builder</a>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
